import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, LogOut, UserCheck, LayoutGrid, Upload, PencilLine, Download, MessageCircle, FileWarning, AlertTriangle, CalendarClock, Layers } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Progress } from "../../components/ui/progress";
import { Logo, UrgencyBadge } from "../../components/joe/Badges";
import { Scorecard, EquityCard, VerificationList } from "../../components/joe/Scorecard";
import { ConflictCard } from "../../components/joe/ConflictCard";
import { OptionsCompare } from "../../components/joe/OptionsCompare";
import { DocumentVault } from "../../components/joe/DocumentVault";
import { Handoff, AccountCard } from "../../components/joe/Handoff";
import { JoeChat } from "../../components/joe/JoeChat";
import { ScamBanner } from "./Interview";
import { api, store } from "../../lib/api";
import { Guide, CREW } from "../../lib/brand";

const TabGuide = ({ who, text }) => (
  <div className="mb-5 flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`tab-guide-${who}`}>
    <Guide who={who} size={52} label={false} />
    <div><p className="font-display text-lg font-bold uppercase" style={{ color: CREW[who].color }}>{CREW[who].name}, {CREW[who].role.toLowerCase()}</p><p className="text-sm text-slate-600">{text}</p></div>
  </div>
);

const Stat = ({ icon: Icon, n, l, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <Icon className="h-5 w-5 text-amber-600" /><p className="mt-2 font-display text-3xl font-extrabold">{n}</p><p className="text-sm text-slate-600">{l}</p>
  </div>
);

const CTA = ({ setTab, nav }) => {
  const btns = [["Talk to my specialist", UserCheck, () => setTab("specialist")], ["Review my options", LayoutGrid, () => setTab("options")],
    ["Upload missing documents", Upload, () => setTab("documents")], ["Correct my information", PencilLine, () => nav("/review")],
    ["Download my situation report", Download, () => nav("/case/report")], ["Continue with Joe", MessageCircle, () => setTab("joe")]];
  return (
    <section className="rounded-3xl bg-slate-900 p-6 text-white sm:p-8" data-testid="final-cta">
      <h2 className="font-display text-2xl font-extrabold sm:text-3xl">YOU DON'T NEED TO GUESS YOUR NEXT MOVE.</h2>
      <p className="mt-2 text-slate-300">Your situation has been organized. Now choose your next step.</p>
      <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {btns.map(([l, Icon, fn]) => (
          <button key={l} data-testid={`cta-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={fn}
            className="tap flex min-h-[52px] items-center gap-2 rounded-xl bg-white/10 px-4 text-left text-sm font-bold uppercase tracking-wide hover:bg-white hover:text-slate-900">
            <Icon className="h-4 w-4 text-amber-300" /> {l}
          </button>
        ))}
      </div>
    </section>
  );
};

export default function CaseHub() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const tab = sp.get("tab") || "overview";
  const setTab = (t) => setSp({ tab: t });
  const load = useCallback(() => api.get("/case").then(({ data }) => setData(data)).catch(() => nav("/login")), [nav]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  const an = data.analysis;
  const upcoming = an.urgency.events.filter((e) => e.days_remaining !== null && e.days_remaining >= 0 && e.days_remaining <= 60).length;
  const logout = () => { store.token = null; store.caseToken = null; nav("/start"); };

  return (
    <div className="grain min-h-screen">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3">
          <Logo />
          <div className="flex items-center gap-3"><span className="hidden font-mono text-xs text-slate-500 sm:inline" data-testid="case-lead-id">{data.lead_id}</span>
            <button data-testid="case-logout-btn" onClick={logout} className="text-slate-500 hover:text-slate-900"><LogOut className="h-4 w-4" /></button></div>
        </div>
      </header>
      <main className="relative z-10 mx-auto max-w-5xl px-5 pb-20 pt-8">
        <section className="rise">
          <p className="font-mono text-xs uppercase tracking-[.2em] text-amber-700">{data.intake_completed ? "Review organized" : "Case memory"}</p>
          <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl" data-testid="welcome-back-heading">Welcome back. Your AskJoe review is {data.progress}% complete.</h1>
          <Progress value={data.progress} className="mt-4 h-2" />
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <UrgencyBadge color={an.urgency.color} label={an.urgency.label} />
            {an.urgency.days_remaining !== null && <span className="font-mono text-sm" data-testid="case-days-remaining">{an.urgency.days_remaining} days remaining</span>}
            {!data.intake_completed && <button data-testid="continue-review-btn" onClick={() => nav("/review")} className="tap rounded-full bg-slate-900 px-4 py-2 text-sm font-bold uppercase text-white">Continue where I stopped</button>}
          </div>
        </section>
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat icon={FileWarning} n={an.docs_missing.length} l="documents missing" testId="stat-docs-missing" />
          <Stat icon={AlertTriangle} n={an.needs_verification} l="answers requiring verification" testId="stat-verification" />
          <Stat icon={CalendarClock} n={upcoming} l="approaching deadline(s)" testId="stat-deadlines" />
          <Stat icon={Layers} n={an.solutions.length} l="potential solution categories" testId="stat-solutions" />
        </div>
        {an.scam.detected && <div className="mt-6"><ScamBanner /></div>}
        {!data.has_account && <div className="mt-6"><AccountCard onDone={load} /></div>}
        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-transparent p-0">
            {[["overview", "Overview"], ["options", "My Options"], ["documents", "Documents"], ["specialist", "Specialist"], ["joe", "Ask Joe"]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`case-tab-${v}`} className="rounded-full border border-slate-300 px-4 py-2 font-semibold data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white">{l}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="overview" className="mt-6 space-y-6">
            {an.conflicts.filter((c) => !c.resolved).map((c) => <ConflictCard key={c.id} c={c} onResolved={load} />)}
            <div className="grid gap-6 lg:grid-cols-2"><Scorecard sc={an.scorecard} /><div className="space-y-2"><Guide who="terry" size={36} testId="equity-guide-terry" /><EquityCard eq={an.equity} cf={an.cashflow} /></div></div>
            <VerificationList items={an.verification} />
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="next-actions">
              <h3 className="font-display text-lg font-bold">Preliminary Resolution Roadmap</h3>
              <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-[15px]">{an.next_actions.map((a) => <li key={a}>{a}</li>)}</ol>
              <p className="mt-3 text-sm text-slate-600">Recommended specialist(s): <b>{an.specialists.map((s) => s.label).join(", ")}</b></p>
            </div>
            <CTA setTab={setTab} nav={nav} />
          </TabsContent>
          <TabsContent value="options" className="mt-6"><TabGuide who="rusty" text="I line up the possible paths side by side so you can see the pros, cons, costs and timing of each. Fiona helps with the selling paths." /><OptionsCompare analysis={an} onChange={load} /></TabsContent>
          <TabsContent value="documents" className="mt-6 space-y-4">
            <TabGuide who="claire" text="Upload your notices and statements. I read each one, pull out the dates and amounts, and flag anything that doesn't match your answers." />
            {an.conflicts.filter((c) => !c.resolved).map((c) => <ConflictCard key={c.id} c={c} onResolved={load} />)}
            <DocumentVault data={data} onChange={load} />
          </TabsContent>
          <TabsContent value="specialist" className="mt-6"><TabGuide who="buddy" text="I'll make sure the right human specialist gets your organized situation — only with your permission." /><Handoff data={data} onChange={load} /></TabsContent>
          <TabsContent value="joe" className="mt-6"><TabGuide who="joe" text="Ask me anything about your situation. Oliver the Legal Owl reminds me when something needs an attorney." /><JoeChat /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
